import { Component, OnInit } from '@angular/core';
import { SearchForm } from '../model/search-form';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { SearchResult } from '../model/search-result';
import { SearchService } from '../search.service';
import { map, Observable, of, switchMap } from 'rxjs';
import { FlightItemComponent } from 'app/features/flights/flight-item/flight-item.component';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { LogbookLeg } from '@shared/models/activity-summary/logbook-leg';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { SearchResultDialogComponent } from 'app/features/search/search-result-dialog/search-result-dialog.component';
import { SignalsService } from '@shared/services/signals/signals.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { convertAircraftType } from '@shared/utilities/functions';
import { FlightdetailService } from 'app/features/flights/flight-detail/service/flightdetail.service';
import { CrewDetail } from 'app/features/flights/flight-detail/model/crew-detail';
import { FormatDatePipe } from '@shared/pipes/format-time.pipe';
import { AccordionModule } from 'primeng/accordion';
import { AppSettings } from '@shared/models/settings/app-settings';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { DateTime } from 'luxon';

interface PageEvent {
  first?: number;
  page?: number;
  pageCount?: number;
  rows?: number;
}

@Component({
  selector: 'app-search-result',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbComponent,
    FlightItemComponent,
    FormatDatePipe,
    AccordionModule
  ],
  templateUrl: './search-result.component.html',
  styleUrl: './search-result.component.scss'
})
export class SearchResultComponent implements OnInit {

  page: number = this.searchService.searchResultsPage$();

  form?: SearchForm;
  formCopy?: SearchForm; //original form before filtering
  searchResponse$?: Observable<RequestState<SearchResult>>;
  empNumber$ = this.signalService.employeeNumber$;
  nonFilteredResults?: SearchResult;
  appSettings?: AppSettings;
  ref: DynamicDialogRef | undefined;

  constructor(
    private router: Router,
    private searchService: SearchService,
    private signalService: SignalsService,
    private flightDetailService: FlightdetailService,
    private errorLogService: ErrorLogService
  ) { }

  /**
   * Initializes the component by retrieving search-related data from session storage.
   * If search results and non-filtered results are found in session storage, it sets the component's
   * state with these results. Otherwise, it processes the search form data and initiates a search request.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.appSettings = JSON.parse(localStorage.getItem('appSettings') || '');
    const searchForm = sessionStorage.getItem('form');
    const searchResponse = sessionStorage.getItem('searchResponse');
    const nonFilteredResults = sessionStorage.getItem('nonFilteredResults');
    
    if (searchResponse && nonFilteredResults) {
      this.nonFilteredResults = JSON.parse(nonFilteredResults)
      this.formCopy = searchForm ? JSON.parse(searchForm) : undefined;
      this.updateSearchPage(this.page);
    }

    else if (searchForm) {
      const form = JSON.parse(searchForm);
      this.formCopy = JSON.parse(searchForm);
      if (form.crewFirstName || form.crewLastName) {
        if (form.seat) {
          form.seat = null;
        }
      }
      // if (form.stationCode || (form.departureStation && form.arrivalStation)) {
      //   if (form.stationCode) {
      //     form.stationCode = form.stationCode.toUpperCase();
      //   }
      //   else if (form.departureStation && form.arrivalStation) {
      //     form.departureStation = form.departureStation.toUpperCase();
      //     form.arrivalStation = form.arrivalStation.toUpperCase();
      //   }
      // }
      this.form = form;
      this.getSearchResults(form);
    }
  }

  getSearchResults(searchForm: SearchForm) {
    /**Get employee number to conduct search call to api */
    this.searchResponse$ = this.empNumber$.pipe(
      switchMap((empNum) => {
        return this.searchService.makeSearchRequest(searchForm, empNum)
          .pipe(
            switchMap((results) => {
              /** if no result from api and return dialog and empty sequence items */
              if (!results || !results.response) {
                //this.errorLogService.logError('No response from search service');
                return of(new RequestState<SearchResult>(
                  RequestStatus.IsCompleteNoResponse,
                  { timestamp: '', hasNext: false, items: [] }
                ));
              }
            
              if (results.response?.items.length === 0) {
                this.noResultDialog();
              }

              const crewEmpNumbers = this.getCrewEmpNumbersFor(results.response!);

              /** Get crew details for all crew in the returned search result*/
              return this.flightDetailService.getCrewDetailsFor(crewEmpNumbers, `crewDetails`)
                .pipe(
                  map((crewDetails) => {

                    if (!crewDetails.response || !crewDetails.isLoading && crewDetails.response.length === 0) {
                      return new RequestState<SearchResult>(
                        RequestStatus.IsCompleteNoResponse,
                        results.response
                      );
                    }

                    /** Filter results based on user enter criteria */
                    // else if (results.response && crewDetails.response && 
                    const filteredResults = this.filterResults(
                      searchForm,
                      results.response!,
                      crewDetails.response
                    );
                    /** Reverse the legs of each sequence to be in order of latest
                     * and return the reversed filtered results 
                     */
                    const reversedLegsFilteredResults = this.reverseLegsFilteredResults(filteredResults);
                    return new RequestState<SearchResult>(
                      RequestStatus.IsCompleteWithResponse,
                      reversedLegsFilteredResults
                    );
                  })
                );
            })
          );
      }),
    );
  }

  /**
   * Reverses the legs of each sequence in the search results to display the most recent leg first.
   *
   * @param results - The search results containing flight information.
   * @returns The search results with the legs of each sequence reversed
   */
  reverseLegsFilteredResults(results: SearchResult): SearchResult {
    results;
    if (this.appSettings?.sortOrder === 1) {
      for (const item of results.items) {
        item.dutyPeriodSummaries.reverse();
        for (const duty of item.dutyPeriodSummaries) {
          duty.legs.reverse();
        }
      };

    }
    return results
  }

  /**
   * Extracts the employee numbers of the flight crew from the given search results.
   *
   * @param results - The search results containing flight information.
   * @returns An array of employee numbers for the flight crew.
   */
  getCrewEmpNumbersFor(results: SearchResult): string[] {
    const resultsCopy: SearchResult = JSON.parse(JSON.stringify(results));
    return resultsCopy.items.flatMap(item =>
      item.dutyPeriodSummaries.flatMap(duty =>
        duty.legs.flatMap(leg =>
          leg.flightCrew.map(crew => crew.employeeNumber)
        )
      )
    );
  }

  /**
   * Filters the search results based on the provided search form criteria.
   * 
   * @param {SearchForm} form - The search form containing the filtering criteria.
   * @param {SearchResult} results - The original search results to be filtered.
   * @param {CrewDetail[]} crewDetails - The details of the crew members to be used for filtering.
   * @returns {SearchResult} - The filtered search results.
   * 
   * The function performs the following steps:
   * 1. Creates a deep copy of the results to preserve the original data.
   * 2. Stores the deep copy in `this.nonFilteredResults` if results are provided.
   * 3. Returns an empty SearchResult object if no results are provided.
   * 4. Filters the results based on the criteria provided in the search form:
   *    - Filters duty period summaries and their legs based on seat, equipment type, flight number, equipment registration, employee number, crew first name, and crew last name.
   *    - Returns only the items that have duty period summaries with legs matching the criteria.
   * 5. Returns the original results if no filtering criteria are provided.
   */
  filterResults(form: SearchForm, results: SearchResult, crewDetails: CrewDetail[]): SearchResult {
    
    const deepCopy = JSON.parse(JSON.stringify(results));
    /** Nonfiltered used to pass full unfiltered sequence to sequence detail view */
    if (results) {
      this.nonFilteredResults = deepCopy;
    }

    if (!results) {
      return { timestamp: '', hasNext: false, items: [] };
    }
    else if (
      results &&
      (
        form.date ||
        form.seat ||
        form.equipmentNumber ||
        form.flightNumber?.toString() ||
        form.equipmentReg ||
        form.employeeNumber ||
        form.crewFirstName ||
        form.crewLastName) ||
        form.stationCode ||
        (form.departureStation &&
        form.arrivalStation)
    ) {
      
      let resultsCopy = results.items.map(item => {
        item.dutyPeriodSummaries = item.dutyPeriodSummaries.map(duty => {
          duty.legs = duty.legs.filter(leg => {
            /** Return leg if match from user entered form criteria */
            return (
              (!form.seat || item.seat === form.seat) &&
              (!form.date || 
                DateTime.fromISO(leg.departure.actual.utc).toFormat('yyyy-MM-dd') === DateTime.fromISO(form.date || '').toFormat('yyyy-MM-dd') ) &&
              (!form.date || 
                DateTime.fromISO(leg.departure.scheduled.utc).toFormat('yyyy-MM-dd') === DateTime.fromISO(form.date || '').toFormat('yyyy-MM-dd') ) &&
              (!form.date || 
                DateTime.fromISO(leg.departure.actual.local).toFormat('yyyy-MM-dd') === DateTime.fromISO(form.date || '').toFormat('yyyy-MM-dd') ) &&
              (!form.date || 
                DateTime.fromISO(leg.departure.scheduled.local).toFormat('yyyy-MM-dd') === DateTime.fromISO(form.date || '').toFormat('yyyy-MM-dd') ) &&  
              (!form.equipmentNumber || leg.aircraft?.type?.equipmentNumber === form.equipmentNumber) &&
              (!form.flightNumber?.toString() || leg.flightNumber === form.flightNumber) &&
              (!form.equipmentReg || leg.aircraft?.id === form.equipmentReg) &&
              (!form.employeeNumber || leg.flightCrew.some(crew => crew.employeeNumber === form.employeeNumber)) &&
              (!form.crewLastName || this.matchCrewName(leg, form, crewDetails)) &&
              (!form.crewFirstName || this.matchCrewName(leg, form, crewDetails)) &&
              (!form.stationCode || 
                leg.departureStation.code === form.stationCode || 
                leg.arrivalStation.code === form.stationCode) &&
              ((!form.departureStation && !form.arrivalStation) || leg.departureStation.code === form.departureStation && leg.arrivalStation.code === form.arrivalStation)
            );
          });
          return duty;
        }).filter(duty => duty.legs.length > 0);
        return item;
      }).filter(item => item.dutyPeriodSummaries.length > 0);

      // Return object of SearchResult with filtered results based on form criteria
      const filteredResults: SearchResult = {
        timestamp: '',
        hasNext: results.hasNext,
        items: resultsCopy
      }
      
      return filteredResults;

    }
    else {
      //If none of the criteria are provided, return the original results
      return results;
    }
  }

  /**
   * Checks if the crew name matches the search form criteria.
   * 
   * @param {LogbookLeg} leg - The logbook leg containing the crew information.
   * @param {SearchForm} form - The search form containing the crew name criteria.
   * @param {CrewDetail[]} crewDetail - The details of the crew members to be used for filtering.
   * @returns {boolean} - True if the crew name matches the search form criteria, false otherwise.
   * 
   * The function performs the following steps:
   * 1. Iterates over the crew details to find a match with the crew name criteria.
   * 2. Checks if the crew last name matches the search form criteria.
   * 3. Checks if the crew first name matches the search form criteria.
   * 4. Checks if the crew full name matches the search form criteria.
   * 5. Checks if the crew is in the leg and matches the search form criteria.
   * 6. Returns true if the crew name matches the search form criteria, false otherwise.
   */
  matchCrewName(leg: LogbookLeg, form: SearchForm, crewDetail: CrewDetail[]): boolean {
    return crewDetail?.some(crew => {
      const matchesLastName = form.crewLastName &&
        crew?.lastName?.toUpperCase() === form.crewLastName.toUpperCase();

      const matchesFirstName = form.crewFirstName &&
        crew?.firstName?.toUpperCase().startsWith(form.crewFirstName[0].toUpperCase());

      const matchesFullName = form.crewFirstName &&
        crew?.firstName.toUpperCase() === form.crewFirstName.toUpperCase();

      const isCrewInLeg = leg.flightCrew?.some(legCrew => legCrew?.employeeNumber === crew?.username);
      return isCrewInLeg && (matchesLastName || matchesFullName || matchesFirstName);
    });

  }

  /**
   * Converts an equipment type code to its corresponding aircraft type description.
   *
   * @param code - The equipment type code to convert. Can be a string or null.
   * @returns The long description of the aircraft type if the code is valid, otherwise null.
   */
  convertEquipmentTypeCode(code: string | null): string | null {
    if (!code) {
      return null;
    }
    const aircraftType = convertAircraftType(code);
    return aircraftType ? aircraftType.long : '';
  }


  /**
   * Handles the event when a sequence is clicked.
   * 
   * @param selectedSequence - The sequence that was selected.
   * @param searchResults - The search results that may be null or undefined.
   * @param to - The target route to navigate to.
   * 
   * This method filters out the selected sequence from the non-filtered results
   * to display the full sequence details when the user clicks on the sequence number.
   * It then stores various items in the session storage and navigates to the 'sequence-detail' route.
   */
  onSequenceClicked(
    selectedSequence: Sequences,
    index: number,
    searchResults: SearchResult | null | undefined,
    to: string): void {
    
    /** filter out the selected sequence on nonFiltered seq so as to display the full sequence
     * when user clicks on sequence number to see full sequence details
     */
    const nonFilteredSequence = this.nonFilteredResults?.items.filter((item, seqIndex) => {

      return (
        item.sequenceNumber === selectedSequence.sequenceNumber &&
        seqIndex === index
      )
    }
    );

    sessionStorage.setItem('to', JSON.stringify(to)); //to: 'search-result-seq'
    sessionStorage.setItem('nonFilteredSequence', JSON.stringify(nonFilteredSequence));
    sessionStorage.setItem('nonFilteredResults', JSON.stringify(this.nonFilteredResults));
    sessionStorage.setItem('searchResponse', JSON.stringify(searchResults));
    sessionStorage.setItem('form', JSON.stringify(this.formCopy));

    this.router.navigate(['sequence-detail']);
  }


  /**
   * Handles the event when a leg is clicked in the search results.
   * Stores the selected leg, sequence, search results, and form data in session storage,
   * then navigates to the flight detail page.
   *
   * @param selectedLeg - The selected leg from the logbook.
   * @param sequence - The sequence associated with the selected leg.
   * @param searchResults - The search results that include the selected leg.
   * @param to - The destination string, typically 'search-result-leg'.
   * @returns void
   */
  onLegClicked(
    selectedLeg: LogbookLeg,
    sequence: Sequences,
    searchResults: SearchResult,
    to: string): void {

    sessionStorage.setItem('to', JSON.stringify(to)); //to: 'search-result-leg'
    sessionStorage.setItem('leg', JSON.stringify(selectedLeg));
    sessionStorage.setItem('sequence', JSON.stringify(sequence));
    sessionStorage.setItem('searchResponse', JSON.stringify(searchResults));
    sessionStorage.setItem('nonFilteredResults', JSON.stringify(this.nonFilteredResults));
    sessionStorage.setItem('form', JSON.stringify(this.formCopy));

    this.router.navigate(['flight-detail'])
  }

  noResultDialog(): void {
    this.searchService.openDialog(SearchResultDialogComponent)
  }

  /**
   * Handles the click event for the "Previous" button.
   * Decrements the current page number if it is greater than 0
   * and updates the search results to reflect the new page.
   */
  onPrevClick(): void {
    if (this.page > 0) {
      this.page -= 1;
      this.searchService.searchResultsPage$.set(this.page);
      this.updateSearchPage(this.page);
    }
  }

  /**
   * Handles the click event for the "Next" button.
   * Increments the current page number if there are more results available
   * and updates the search results to reflect the new page.
   */
  onNextClick(results: SearchResult): void {
    if (results.hasNext) {
      this.page += 1;
      this.searchService.searchResultsPage$.set(this.page);
      this.updateSearchPage(this.page);
    }

  }

  /**
   * Updates the search results to reflect the current page number.
   */
  updateSearchPage(page: number): void {
    const formCopy = this.formCopy ? JSON.parse(JSON.stringify(this.formCopy)) : undefined;
    formCopy.page = page;
    
    const cachedResults = localStorage.getItem(`searchResults-${page}`);
    const crewDetails = localStorage.getItem('crewDetails');
    
    if (cachedResults) {
      const results: SearchResult = JSON.parse(cachedResults);
      const crew = JSON.parse(crewDetails || '');
      
      
      const filteredResults = this.filterResults(formCopy, results, crew.memberDetails);
      //console.log('filteredResponse', filteredResults);
      this.searchResponse$ = of(
        new RequestState<any>(
          RequestStatus.IsCompleteWithResponse,
          filteredResults
        )
      );
    } else {
      this.getSearchResults(formCopy);
    }
  }

}


/** Below code for future use if the ability to search by other pilot seat positon and name */

// if ((form.crewFirstName || form.crewLastName) && this.formCopy?.seat) {
//   const _form = this.formCopy;
//   console.log('searching by name and seat');
//   return (
//     (!form.equipmentType || leg.aircraft.type.long === this.convertEquipmentTypeCode(form.equipmentType)) &&
//     (!form.flightNumber || +leg.flightNumber === form.flightNumber) &&
//     (!form.equipmentReg || leg.aircraft.id === form.equipmentReg) &&
//     (!form.employeeNumber || leg.flightCrew.some(crew => crew.employeeNumber === form.employeeNumber)) 
//     (!_form.seat || this.matchCrewNameAndSeat(leg, this.formCopy))
//   );
// } else {
//   console.log('searching by user seat only and other criteria');
//   return (
//     (!form.seat || item.seat === form.seat) &&
//     (!form.equipmentType || leg.aircraft.type.long === this.convertEquipmentTypeCode(form.equipmentType)) &&
//     (!form.flightNumber || +leg.flightNumber === form.flightNumber) &&
//     (!form.equipmentReg || leg.aircraft.id === form.equipmentReg) &&
//     (!form.employeeNumber || leg.flightCrew.some(crew => crew.employeeNumber === form.employeeNumber))
//   );
// }